import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const SettingsContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SETTINGS_CACHE_KEY = 'crm_settings_cache';
const defaultSettings = {
  company_name: 'Decedas Global Resources Limited',
  company_address: '',
  company_phone: '',
  company_email: '',
  company_tagline: 'Excellence in Every Transaction',
  company_logo: null,
};

const readCachedSettings = () => {
  try {
    const cached = localStorage.getItem(SETTINGS_CACHE_KEY);
    return cached ? { ...defaultSettings, ...JSON.parse(cached) } : defaultSettings;
  } catch {
    return defaultSettings;
  }
};

const writeCachedSettings = (settings) => {
  try {
    localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(settings));
  } catch {
    // Ignore cache write failures
  }
};

export const SettingsProvider = ({ children }) => {
  const { token } = useAuth();
  const [settings, setSettings] = useState(readCachedSettings);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async (authToken) => {
    if (!authToken) {
      setSettings(readCachedSettings());
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setSettings(response.data);
      writeCachedSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setSettings(readCachedSettings());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings(token);
  }, [fetchSettings, token]);

  const updateSettings = useCallback((newSettings) => {
    setSettings(newSettings);
    writeCachedSettings(newSettings);
  }, []);

  const refreshSettings = useCallback(async () => {
    await fetchSettings(token);
  }, [fetchSettings, token]);

  const value = {
    settings,
    loading,
    updateSettings,
    refreshSettings,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
